/**
 * Dimension association engine (docs/plans/2d/2d-full-parity-plan.md
 * Faz 5.3): DIMREASSOCIATE stores a bind-time snapshot of the source
 * geometry (`data.assocRef = { pts, centroid }`) on the dimension. This
 * watcher replays source GEOMETRY MOVEMENT onto the bound dimension: when a
 * source's centroid shifts (move/grip/stretch — any data change that
 * re-places the shape), the dimension's definition points translate by the
 * same delta, so the label follows the geometry like a real associative
 * 2D dimension. Updates are silent (no history push), matching the
 * product where recompute is not a user command.
 *
 * Booted from the CommandRegistrar alongside the history engine.
 */

import { useAppStore, type Entity } from '../../state/store';
import { centroid, type Pt3 } from './geometryMath';
import { entityToPolyline } from './runners/lib';

/** Shift every point-bearing field of a data bag by a delta. */
function translateBag(data: Record<string, any>, d: [number, number, number]): Record<string, any> {
    const next = { ...data };
    const shift = (p: number[]): number[] => [p[0] + d[0], p[1] + d[1], (p[2] ?? 0) + d[2]];
    ['point1', 'point2', 'textPosition', 'center', 'radiusPoint'].forEach((k) => {
        if (Array.isArray(next[k]) && typeof next[k][0] === 'number') next[k] = shift(next[k]);
    });
    return next;
}

/** Recompute one dimension's measured value from its (moved) definition points. */
function refreshValue(data: Record<string, any>): Record<string, any> {
    const next = { ...data };
    const kind = String(next.type ?? '');
    if ((kind === 'linear' || kind === 'aligned' || kind === 'baseline' || kind === 'continue')
        && Array.isArray(next.point1) && Array.isArray(next.point2) && !next.override) {
        const a = next.point1 as number[], b = next.point2 as number[];
        next.measured = Math.hypot(b[0] - a[0], b[1] - a[1]);
    }
    if ((kind === 'radius' || kind === 'diameter') && Array.isArray(next.center) && Array.isArray(next.radiusPoint)) {
        const c = next.center as number[], p = next.radiusPoint as number[];
        next.radius = Math.hypot(p[0] - c[0], p[1] - c[1]);
    }
    if (kind === 'arcLength' && Array.isArray(next.point1) && Array.isArray(next.point2) && !next.override) {
        next.arcLength = Math.hypot(
            (next.point2[0] as number) - (next.point1[0] as number),
            (next.point2[1] as number) - (next.point1[1] as number)
        );
    }
    return next;
}

let started = false;

export function startDimAssociation(): void {
    if (started) return;
    started = true;
    useAppStore.subscribe((state, prev) => {
        if (state.entities === prev.entities) return;
        const dims = Object.values(state.entities).filter(
            (e) => e.type === 'dimension' && Array.isArray((e.data as Record<string, any>).assocRef?.pts)
                && (e.data as Record<string, any>).assocRef.pts.length >= 2
        ) as Entity[];
        if (dims.length === 0) return;
        dims.forEach((dim) => {
            const ref = dim.data.assocRef as { pts: number[][]; centroid: number[] };
            const srcId = String((dim.data.assoc as string[])?.[0] ?? '');
            const src = state.entities[srcId];
            if (!src) return;
            const wasDirty = prev.entities[srcId]?.data !== src.data;
            if (!wasDirty) return;
            const poly = entityToPolyline(src);
            if (!poly || poly.length < 2) return;
            const now = centroid(poly);
            const old = ref.centroid as [number, number, number];
            const d: [number, number, number] = [now[0] - old[0], now[1] - old[1], (now[2] ?? 0) - (old[2] ?? 0)];
            if (Math.hypot(d[0], d[1], d[2]) < 1e-6) return;
            // Apply the delta once to this dimension's definition points and
            // rebaseline the snapshot so a further edit measures from here.
            useAppStore.getState().updateEntity(dim.id, {
                data: refreshValue({
                    ...translateBag(dim.data, d),
                    assocRef: { pts: poly.map((p) => [...p] as Pt3), centroid: now },
                }),
            });
        });
    });
}
